import { useState, useEffect, useCallback } from 'react';
import { Server, Plus, Edit, Trash2, Eye, CheckCircle, XCircle, Lock, Key, FolderOpen, File, Folder, RefreshCw, Upload } from 'lucide-react';
import Card from '../components/common/Card';
import Table from '../components/common/Table';
import SearchBar from '../components/common/SearchBar';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import Modal from '../components/common/Modal';
import Input from '../components/common/Input';
import { getSftpSites, getSftpSiteById, createSftpSite, updateSftpSite, deleteSftpSite, browseSftpFiles } from '../services/sftpServiceSwitch';
import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import './SftpSites.css';

export default function SftpSites() {
  const { user } = useAuth();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [browseSiteId, setBrowseSiteId] = useState(null);
  const [browseSiteName, setBrowseSiteName] = useState('');
  const [selectedSite, setSelectedSite] = useState(null);
  const [viewSiteDetails, setViewSiteDetails] = useState(null);
  const [toast, setToast] = useState(null);

  const isAdmin = user?.role === 'admin';

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSftpSites({ search: searchTerm, page: currentPage, limit: itemsPerPage });
      setSites(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (err) {
      setError(err.message || 'Failed to load SFTP sites');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, currentPage]);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const handleSearch = (e) => {
    setSearchTerm(typeof e === 'string' ? e : e.target.value);
    setCurrentPage(1);
  };

  const handleView = async (site) => {
    try {
      const details = await getSftpSiteById(site.id);
      setViewSiteDetails(details);
      setShowViewModal(true);
    } catch {
      showToast('Failed to load site details', 'error');
    }
  };

  const handleEdit = async (site) => {
    try {
      const details = await getSftpSiteById(site.id);
      setSelectedSite(details);
      setShowEditModal(true);
    } catch {
      showToast('Failed to load site details', 'error');
    }
  };

  const handleDelete = async (site) => {
    if (!window.confirm(`Delete "${site.name}"? This cannot be undone.`)) return;
    try {
      await deleteSftpSite(site.id);
      showToast('SFTP site deleted', 'success');
      fetchSites();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to delete. Site may be in use by reports.', 'error');
    }
  };

  const handleBrowse = (site) => {
    setBrowseSiteId(site.id);
    setBrowseSiteName(site.name);
    setShowBrowseModal(true);
  };

  const showToast = (message, type = 'success') => setToast({ message, type });
  const handleSuccess = (message) => { showToast(message, 'success'); fetchSites(); };

  const columns = [
    {
      key: 'name', label: 'Site Name', sortable: false,
      render: (site) => (
        <div className="sftp-name-cell"><Server size={18} /><span>{site.name}</span></div>
      )
    },
    { key: 'description', label: 'Description', sortable: false },
    {
      key: 'isActive', label: 'Status', sortable: false,
      render: (site) => (
        <span className={`status-badge ${site.isActive ? 'status-active' : 'status-inactive'}`}>
          {site.isActive ? <><CheckCircle size={14} /> Active</> : <><XCircle size={14} /> Inactive</>}
        </span>
      )
    },
    {
      key: 'reportCount', label: 'Reports', sortable: false,
      render: (site) => <span>{site.reportCount}</span>
    },
    {
      key: 'actions', label: 'Actions', sortable: false,
      render: (site) => (
        <div className="action-buttons">
          <button className="action-btn view-btn" onClick={() => handleView(site)} title="View"><Eye size={16} /></button>
          <button className="action-btn browse-btn" onClick={() => handleBrowse(site)} title="Browse Files"><FolderOpen size={16} /></button>
          {isAdmin && (
            <>
              <button className="action-btn edit-btn" onClick={() => handleEdit(site)} title="Edit"><Edit size={16} /></button>
              <button className="action-btn archive-btn" onClick={() => handleDelete(site)} title="Delete"><Trash2 size={16} /></button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="sftp-sites-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><Server size={32} /> SFTP Sites</h1>
            <p className="page-subtitle">Manage SFTP delivery destinations</p>
          </div>
          {isAdmin && (
            <Button variant="primary" onClick={() => setShowCreateModal(true)} icon={<Plus size={20} />}>Add SFTP Site</Button>
          )}
        </div>
      </div>

      <Card className="sftp-card">
        <div className="sftp-controls">
          <SearchBar value={searchTerm} onChange={handleSearch} placeholder="Search SFTP sites..." />
        </div>

        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} onRetry={() => window.location.reload()} />}

        {!loading && !error && (
          <>
            <Table columns={columns} data={sites} emptyMessage="No SFTP sites found" />
            {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
          </>
        )}
      </Card>

      {/* Create Modal */}
      <SftpFormModal isOpen={showCreateModal} onClose={() => setShowCreateModal(false)} onSuccess={handleSuccess} mode="create" />

      {/* Edit Modal */}
      <SftpFormModal isOpen={showEditModal} onClose={() => { setShowEditModal(false); setSelectedSite(null); }} onSuccess={handleSuccess} mode="edit" site={selectedSite} />

      {/* View Modal */}
      {viewSiteDetails && (
        <ViewSftpModal isOpen={showViewModal} onClose={() => { setShowViewModal(false); setViewSiteDetails(null); }} site={viewSiteDetails} />
      )}

      {/* Browse Files Modal */}
      <BrowseFilesModal
        isOpen={showBrowseModal}
        onClose={() => { setShowBrowseModal(false); setBrowseSiteId(null); }}
        siteId={browseSiteId}
        siteName={browseSiteName}
      />

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}

/** View modal — shows non-sensitive connection details */
function ViewSftpModal({ isOpen, onClose, site }) {
  const conn = site.connection || {};
  const authLabels = { password: 'Password', private_key: 'Private Key', private_key_passphrase: 'Private Key + Passphrase' };
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="SFTP Site Details" size="md"
      footer={<div className="modal-actions"><Button variant="secondary" onClick={onClose}>Close</Button></div>}>
      <div className="sftp-details">
        <div className="detail-row"><span className="detail-label">Name</span><span className="detail-value">{site.name}</span></div>
        <div className="detail-row"><span className="detail-label">Description</span><span className="detail-value">{site.description || '-'}</span></div>
        <div className="detail-row"><span className="detail-label">Status</span><span className="detail-value">{site.isActive ? 'Active' : 'Inactive'}</span></div>
        <div className="detail-row"><span className="detail-label">Reports Using</span><span className="detail-value">{site.reportCount}</span></div>

        <div className="detail-section-header"><Lock size={16} /> Connection Details</div>
        <div className="detail-row"><span className="detail-label">Host</span><span className="detail-value mono">{conn.host || '-'}</span></div>
        <div className="detail-row"><span className="detail-label">Port</span><span className="detail-value">{conn.port || 22}</span></div>
        <div className="detail-row"><span className="detail-label">Username</span><span className="detail-value mono">{conn.username || '-'}</span></div>
        <div className="detail-row"><span className="detail-label">Remote Path</span><span className="detail-value mono">{conn.remotePath || '-'}</span></div>
        <div className="detail-row"><span className="detail-label">Auth Method</span><span className="detail-value">{authLabels[conn.authMethod] || 'Password'}</span></div>
        <div className="detail-row"><span className="detail-label">Credentials</span><span className="detail-value">{conn.hasCredentials ? '✓ Configured' : '✗ Not set'}</span></div>
        {conn.authMethod === 'private_key_passphrase' && (
          <div className="detail-row"><span className="detail-label">Passphrase</span><span className="detail-value">{conn.hasPassphrase ? '✓ Set' : '✗ Not set'}</span></div>
        )}
        {conn.hostKeyFingerprint && (
          <div className="detail-row"><span className="detail-label">Host Key Fingerprint</span><span className="detail-value mono">{conn.hostKeyFingerprint}</span></div>
        )}
        <div className="detail-row"><span className="detail-label">Timeout</span><span className="detail-value">{conn.timeout || 30}s</span></div>
        {conn.notes && (
          <div className="detail-row"><span className="detail-label">Notes</span><span className="detail-value">{conn.notes}</span></div>
        )}
      </div>
    </Modal>
  );
}

/** Reusable form modal for create/edit with connection credential fields */
function SftpFormModal({ isOpen, onClose, onSuccess, mode, site }) {
  const { authorisedTeams, activeTeamId } = useTeam();
  const realTeams = authorisedTeams.filter(t => t.id !== null);

  const [formData, setFormData] = useState({
    name: '', description: '',
    host: '', port: '22', username: '', password: '', privateKey: '', passphrase: '',
    remotePath: '', authMethod: 'password',
    hostKeyFingerprint: '', timeout: '30', notes: '',
    teamId: activeTeamId || ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (site && mode === 'edit') {
      setFormData({
        name: site.name || '',
        description: site.description || '',
        host: site.connection?.host || '',
        port: String(site.connection?.port || 22),
        username: site.connection?.username || '',
        password: '',
        privateKey: '',
        passphrase: '',
        remotePath: site.connection?.remotePath || '',
        authMethod: site.connection?.authMethod || 'password',
        hostKeyFingerprint: site.connection?.hostKeyFingerprint || '',
        timeout: String(site.connection?.timeout || 30),
        notes: site.connection?.notes || ''
      });
    } else if (mode === 'create') {
      setFormData({
        name: '', description: '', host: '', port: '22', username: '',
        password: '', privateKey: '', passphrase: '', remotePath: '',
        authMethod: 'password', hostKeyFingerprint: '', timeout: '30', notes: '',
        teamId: activeTeamId || ''
      });
    }
  }, [site, mode, isOpen, activeTeamId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (mode === 'create' && !formData.teamId) errs.teamId = 'Select a team';
    if (!formData.name.trim()) errs.name = 'Site name is required';
    if (!formData.host.trim()) errs.host = 'Host is required';
    if (!formData.username.trim()) errs.username = 'Username is required';
    // Credentials only required on create — on edit, blank means "keep current"
    if (mode === 'create') {
      if (formData.authMethod === 'password' && !formData.password) errs.password = 'Password is required';
      if (formData.authMethod === 'private_key' && !formData.privateKey.trim()) errs.privateKey = 'Private key is required';
      if (formData.authMethod === 'private_key_passphrase' && !formData.privateKey.trim()) errs.privateKey = 'Private key is required';
      if (formData.authMethod === 'private_key_passphrase' && !formData.passphrase) errs.passphrase = 'Passphrase is required';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      if (mode === 'create') {
        await createSftpSite(formData);
        onSuccess('SFTP site created');
      } else {
        await updateSftpSite(site.id, formData);
        onSuccess('SFTP site updated');
      }
      onClose();
    } catch (err) {
      setErrors({ submit: err.response?.data?.message || err.message || `Failed to ${mode} SFTP site` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { if (!loading) { setErrors({}); onClose(); } }}
      title={mode === 'create' ? 'Add SFTP Site' : 'Edit SFTP Site'} size="lg" closeOnBackdrop={!loading}
      footer={
        <div className="modal-actions">
          <Button variant="secondary" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" onClick={handleSubmit} loading={loading}>{mode === 'create' ? 'Create' : 'Save'}</Button>
        </div>
      }>
      <form onSubmit={handleSubmit} className="sftp-form">
        {/* Team Selector (create only) */}
        {mode === 'create' && realTeams.length > 1 && (
          <div className="form-group">
            <label htmlFor="sftp-teamId" className="form-label">Team <span className="required">*</span></label>
            <select id="sftp-teamId" name="teamId" value={formData.teamId} onChange={handleChange} className={`form-select ${errors.teamId ? 'error' : ''}`} disabled={loading}>
              <option value="">Select a team</option>
              {realTeams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            {errors.teamId && <span className="error-text">{errors.teamId}</span>}
          </div>
        )}

        {/* Site metadata */}
        <div className="form-section-label">Site Information</div>
        <div className="form-group">
          <label htmlFor="sftp-name" className="form-label">Site Name <span className="required">*</span></label>
          <Input id="sftp-name" name="name" value={formData.name} onChange={handleChange} placeholder="Finance SFTP" error={errors.name} disabled={loading} />
          {errors.name && <span className="error-text">{errors.name}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="sftp-desc" className="form-label">Description</label>
          <textarea id="sftp-desc" name="description" value={formData.description} onChange={handleChange} placeholder="Description of this SFTP site..." className="form-textarea" disabled={loading} rows={2} />
        </div>

        {/* Connection details */}
        <div className="form-section-label"><Key size={16} /> Connection Details</div>
        <div className="form-row">
          <div className="form-group form-group-grow">
            <label htmlFor="sftp-host" className="form-label">Host <span className="required">*</span></label>
            <Input id="sftp-host" name="host" value={formData.host} onChange={handleChange} placeholder="sftp.example.com" error={errors.host} disabled={loading} />
            {errors.host && <span className="error-text">{errors.host}</span>}
          </div>
          <div className="form-group form-group-small">
            <label htmlFor="sftp-port" className="form-label">Port</label>
            <Input id="sftp-port" name="port" type="number" value={formData.port} onChange={handleChange} placeholder="22" disabled={loading} />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="sftp-username" className="form-label">Username <span className="required">*</span></label>
          <Input id="sftp-username" name="username" value={formData.username} onChange={handleChange} placeholder="sftp-user" error={errors.username} disabled={loading} />
          {errors.username && <span className="error-text">{errors.username}</span>}
        </div>
        <div className="form-group">
          <label htmlFor="sftp-remote-path" className="form-label">Remote Path</label>
          <Input id="sftp-remote-path" name="remotePath" value={formData.remotePath} onChange={handleChange} placeholder="/incoming/reports" disabled={loading} />
        </div>

        {/* Auth method toggle */}
        <div className="form-group">
          <label className="form-label">Authentication Method <span className="required">*</span></label>
          <div className="auth-method-toggle">
            <button type="button" className={`toggle-btn ${formData.authMethod === 'password' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, authMethod: 'password' }))} disabled={loading}>
              <Lock size={14} /> Password
            </button>
            <button type="button" className={`toggle-btn ${formData.authMethod === 'private_key' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, authMethod: 'private_key' }))} disabled={loading}>
              <Key size={14} /> Private Key
            </button>
            <button type="button" className={`toggle-btn ${formData.authMethod === 'private_key_passphrase' ? 'active' : ''}`}
              onClick={() => setFormData(prev => ({ ...prev, authMethod: 'private_key_passphrase' }))} disabled={loading}>
              <Key size={14} /> Key + Passphrase
            </button>
          </div>
        </div>

        {formData.authMethod === 'password' && (
          <div className="form-group">
            <label htmlFor="sftp-password" className="form-label">
              Password {mode === 'create' && <span className="required">*</span>}
              {mode === 'edit' && <span className="field-hint-inline">(leave blank to keep current)</span>}
            </label>
            <Input id="sftp-password" name="password" type="password" value={formData.password} onChange={handleChange}
              placeholder={mode === 'edit' ? '••••••••' : 'Enter password'} error={errors.password} disabled={loading} autoComplete="new-password" />
            {errors.password && <span className="error-text">{errors.password}</span>}
          </div>
        )}

        {(formData.authMethod === 'private_key' || formData.authMethod === 'private_key_passphrase') && (
          <>
            <div className="form-group">
              <label htmlFor="sftp-private-key" className="form-label">
                Private Key {mode === 'create' && <span className="required">*</span>}
                {mode === 'edit' && <span className="field-hint-inline">(leave blank to keep current)</span>}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.75rem', fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-color)', background: 'var(--glass-bg)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                  <Upload size={14} /> Upload Key File (.pem, .ppk, .key)
                  <input type="file" accept=".pem,.ppk,.key,.pub,.txt" style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (ev) => {
                          setFormData(prev => ({ ...prev, privateKey: ev.target.result }));
                          if (errors.privateKey) setErrors(prev => ({ ...prev, privateKey: '' }));
                        };
                        reader.readAsText(file);
                      }
                      e.target.value = '';
                    }}
                    disabled={loading}
                  />
                </label>
              </div>
              <textarea id="sftp-private-key" name="privateKey" value={formData.privateKey} onChange={handleChange}
                placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;Paste key or use Upload button above" className="form-textarea mono-textarea"
                disabled={loading} rows={4} />
              {errors.privateKey && <span className="error-text">{errors.privateKey}</span>}
            </div>
            {formData.authMethod === 'private_key_passphrase' && (
              <div className="form-group">
                <label htmlFor="sftp-passphrase" className="form-label">
                  Key Passphrase {mode === 'create' && <span className="required">*</span>}
                  {mode === 'edit' && <span className="field-hint-inline">(leave blank to keep current)</span>}
                </label>
                <Input id="sftp-passphrase" name="passphrase" type="password" value={formData.passphrase} onChange={handleChange}
                  placeholder={mode === 'edit' ? '••••••••' : 'Enter passphrase'} error={errors.passphrase} disabled={loading} autoComplete="new-password" />
                {errors.passphrase && <span className="error-text">{errors.passphrase}</span>}
              </div>
            )}
          </>
        )}

        {/* Advanced / optional settings */}
        <div className="form-section-label">Advanced Settings <span className="field-hint-inline">(all optional)</span></div>
        <div className="form-group">
          <label htmlFor="sftp-host-key" className="form-label">Host Key Fingerprint</label>
          <Input id="sftp-host-key" name="hostKeyFingerprint" value={formData.hostKeyFingerprint} onChange={handleChange}
            placeholder="SHA256:xxxxxxxxxxx..." disabled={loading} />
          <p className="field-hint">Used to verify the server identity and prevent MITM attacks</p>
        </div>
        <div className="form-row">
          <div className="form-group form-group-small">
            <label htmlFor="sftp-timeout" className="form-label">Timeout (s)</label>
            <Input id="sftp-timeout" name="timeout" type="number" value={formData.timeout} onChange={handleChange} placeholder="30" disabled={loading} />
          </div>
          <div className="form-group form-group-grow">
            <label htmlFor="sftp-notes" className="form-label">Notes</label>
            <Input id="sftp-notes" name="notes" value={formData.notes} onChange={handleChange} placeholder="e.g. Requires VPN, contact ops team for access" disabled={loading} />
          </div>
        </div>

        {errors.submit && <div className="form-error">{errors.submit}</div>}
      </form>
    </Modal>
  );
}

/** Browse Files modal — lists files on the remote SFTP server */
function BrowseFilesModal({ isOpen, onClose, siteId, siteName }) {
  const [files, setFiles] = useState([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFiles = useCallback(async (path) => {
    if (!siteId) return;
    try {
      setLoading(true);
      setError(null);
      const result = await browseSftpFiles(siteId, path || undefined);
      setFiles(result.files);
      setCurrentPath(result.path);
    } catch (err) {
      const msg = err.response?.data?.error?.message || err.message || 'Failed to connect';
      setError(msg);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    if (isOpen && siteId) {
      setFiles([]);
      setCurrentPath('');
      setError(null);
      fetchFiles('');
    }
  }, [isOpen, siteId, fetchFiles]);

  const handleNavigate = (dirName) => {
    const newPath = currentPath ? `${currentPath}/${dirName}` : dirName;
    fetchFiles(newPath);
  };

  const handleGoUp = () => {
    if (!currentPath || currentPath === '.' || currentPath === '/') return;
    const parts = currentPath.split('/');
    parts.pop();
    fetchFiles(parts.join('/') || '.');
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleString();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Browse Files — ${siteName || 'SFTP Site'}`} size="lg"
      footer={<div className="modal-actions"><Button variant="secondary" onClick={onClose}>Close</Button></div>}>
      <div className="sftp-browse">
        <div className="browse-toolbar">
          <span className="browse-path mono">{currentPath || '/'}</span>
          <div className="browse-actions">
            <button className="action-btn" onClick={handleGoUp} disabled={loading || !currentPath || currentPath === '.'} title="Go up">
              ↑ Up
            </button>
            <button className="action-btn" onClick={() => fetchFiles(currentPath)} disabled={loading} title="Refresh">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>

        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} />}

        {!loading && !error && (
          <div className="browse-file-list">
            {files.length === 0 ? (
              <p className="empty-message">No files found</p>
            ) : (
              <table className="browse-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Size</th>
                    <th>Modified</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((f) => (
                    <tr key={f.name} className={f.isDirectory ? 'browse-row-dir' : ''}>
                      <td>
                        {f.isDirectory ? (
                          <button className="browse-dir-link" onClick={() => handleNavigate(f.name)}>
                            <Folder size={16} /> {f.name}
                          </button>
                        ) : (
                          <span className="browse-file-name"><File size={16} /> {f.name}</span>
                        )}
                      </td>
                      <td className="browse-size">{f.isDirectory ? '-' : formatSize(f.size)}</td>
                      <td className="browse-date">{formatDate(f.modified)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
